import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { RestartStatus } from '@/features/controller/restart/hooks/useControllerRestartState'
import type { ExecutionResponse } from '@/features/controller/restart/types'
import { ExecutionStatus } from '@/features/controller/restart/types'
import { renderTimestamp } from '@/features/controller/utils/renderTimestamp'

export interface RestartSummary {
  chipColor: 'success' | 'error' | 'warning' | 'default'
  statusLabel: string
  method: string
  completedAt: string
  reason?: string
}

/**
 * Generates summary information for displaying completed restart results.
 * Returns null if restart is in progress or no restart data is available.
 *
 *
 * @param restartStatus - Local restart status from state machine
 * @param lastRestart - The latest restart execution data from the API
 * @returns Summary object or null if summary should not be displayed
 */
export function useRestartSummary(
  restartStatus: RestartStatus,
  lastRestart: ExecutionResponse | undefined,
): RestartSummary | null {
  const { t, i18n } = useTranslation()

  return useMemo(() => {
    // Only show summary when we have a final status (success or error) from local state
    // This matches the logic in RestartProgressSection
    if (restartStatus !== 'success' && restartStatus !== 'error') return null

    if (!lastRestart) return null

    const status = lastRestart.status
    const method = lastRestart.executionMethod
    const completedAt = lastRestart.completedAt
    const reason = lastRestart.statusDetails?.reason

    let chipColor: 'success' | 'error' | 'warning' | 'default'
    let statusLabel = ''

    switch (status) {
      case ExecutionStatus.SUCCEEDED:
        chipColor = 'success'
        statusLabel = t('controllers.restart_status.completed')
        break
      case ExecutionStatus.FAILED:
        chipColor = 'error'
        statusLabel = t('controllers.restart_status.failed')
        break
      case ExecutionStatus.TIMED_OUT:
        chipColor = 'error'
        statusLabel = t('controllers.restart_status.timed_out')
        break
      case ExecutionStatus.CANCELED:
        chipColor = 'warning'
        statusLabel = t('controllers.restart_status.canceled')
        break
      case ExecutionStatus.REJECTED:
        chipColor = 'error'
        statusLabel = t('controllers.restart_status.rejected')
        break
      case ExecutionStatus.REMOVED:
        chipColor = 'warning'
        statusLabel = t('controllers.restart_status.removed')
        break
      case ExecutionStatus.QUEUED:
      case ExecutionStatus.IN_PROGRESS:
        // These statuses should not appear here since we check restartStatus above
        // But handle them explicitly for exhaustiveness
        return null
    }

    return {
      chipColor,
      statusLabel,
      method,
      completedAt: renderTimestamp(completedAt, i18n.language),
      reason,
    }
  }, [restartStatus, lastRestart, t, i18n.language])
}
